import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { Modal } from 'antd';
import {
  CloseIcon,
  Subtitle,
  Button,
  Input,
  Datepicker,
  Dropdown,
  Feedback,
} from '../shared';
import { translate } from '../../lib/lang.helper';
import {
  ILineResource,
  IMusselLineResource,
  IOysterLineResource,
} from '../../entities/farms.entities';
import { useState } from 'react';
import { numToStr, toMillisecond, toSecond } from '../../lib/common.helpers';
import { showFeedback } from '../../store/ui/ui.actions';
import { sendSingleRequest } from '../../apis';
import { loadFarmsData, loadQueueData } from '../../store/farms/farms.actions';
import { selectUtils } from '../../store/utils/utils.selector';

interface IFormData {
  complete_date: number;
  amount: number | null;
  returned_client: string | null;
  shell_condition: string | null;
  shell_length: number | null;
  shell_length_max: number | null;
  comment: string | null;
}

interface Props {
  lineData: ILineResource;
  visible: boolean;
  onClose: () => void;
  onConfirm?: (data: any) => void;
  overConfirm?: (data: any) => Promise<any>;
  data?: any;
}

const conditionOptions = ['Excellent', 'Good', 'Fair', 'Poor'];

const ReturnHarvestModal = ({
  visible,
  onClose,
  lineData,
  onConfirm,
  overConfirm,
  data: paramData,
}: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const customers = useSelector(selectUtils)
    .filter(x => x.type === 'customer')
    .map(x => x.name);

  const [formData, setFormData] = useState<IFormData>(
    paramData
      ? { ...paramData, complete_date: toMillisecond(paramData.complete_date) }
      : {
          complete_date: Date.now(),
          amount: null,
          returned_client: null,
          shell_condition: null,
          shell_length: null,
          shell_length_max: null,
          comment: null,
        },
  );

  const [errorMsg, setErrorMsg] = useState<string>();
  const [errors, setErrors] = useState<any>();
  const [disabled, setDisabled] = useState(false);

  const updateForm = (key: keyof IFormData, value: any) => {
    if (['amount', 'shell_length', 'shell_length_max'].includes(key)) {
      value = value.length <= 0 ? null : Number(value);
    } else if (typeof value === 'string' && value.length <= 0) {
      value = null;
    }
    setFormData(prv => ({ ...prv, [key]: value }));
    setErrors((prv: any) => ({ ...(prv ?? {}), [key]: null }));
  };

  const validForm = () => {
    if (!lineData.growing_cycle) {
      setErrorMsg('_line_empty');
      return null;
    }
    if (formData.amount === null || formData.amount <= 0) {
      const es = translate(lang, 'Please enter returned amount');
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), amount: es }));
      return null;
    }
    if (!formData.returned_client) {
      const es = translate(lang, 'Please enter client name');
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), returned_client: es }));
      return null;
    }
    if (!formData.shell_length) {
      const es = translate(lang, 'Please enter shell length');
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), shell_length: es }));
      return null;
    }
    if (
      formData.shell_length_max !== null &&
      (formData.shell_length === null ||
        formData.shell_length > formData.shell_length_max)
    ) {
      const es = translate(lang, '_invalid_shell_length_range');
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), shell_length: es }));
      return null;
    }
    const currentSeed = lineData.growing_cycle?.main_seed;
    const musselLine = lineData as IMusselLineResource;
    const oysterLine = lineData as IOysterLineResource;
    const lineLength =
      lineData.crop_type === 'MUSSEL'
        ? musselLine.growing_cycle?.current_line_length
        : lineData.crop_type === 'OYSTER'
        ? oysterLine.growing_cycle?.current_basket_count
        : undefined;

    let res = {
      ...formData,
      seeding_id: paramData?.seeding_id ?? currentSeed?.id,
      season_name: currentSeed?.season_name,
      line_id: lineData.id,
      complete_date: toSecond(formData.complete_date),
      is_returned: true,
      is_final: false,
      is_full: true,
      line_length: paramData?.line_length ?? lineLength,
    };
    setErrors(undefined);

    return res;
  };

  const confirmClick = async () => {
    const form = validForm();
    if (!form) return;

    setDisabled(true);
    if (overConfirm) {
      const res = await overConfirm(form);
      if (!res.status) {
        setErrorMsg(res.data?.message ?? 'Request failed');
        if (res.data?.errors) setErrors(res.data.errors);
        setDisabled(false);
      }
      return;
    }
    const res = await sendSingleRequest(
      form,
      'POST',
      paramData
        ? `api/queue/confirm-harvest/${paramData.crop_type}`
        : 'api/farm/line/harvest-complete',
      true,
    );

    if (res.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(
            lang,
            res.data?.message ?? 'Spats returned successfully',
          ),
        }),
      );
      dispatch(loadFarmsData());
      dispatch(loadQueueData());
      if (onConfirm) {
        onConfirm(res.data);
      }
      onClose();
    } else {
      setErrorMsg(res.data?.message ?? 'Request failed');
      if (res.data?.errors) setErrors(res.data.errors);
      setDisabled(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      closable
      closeIcon={<CloseIcon />}
      footer={null}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Return Spats To Client')}
          </Subtitle>
        </div>

        {!!errorMsg && (
          <Feedback
            message={translate(lang, errorMsg)}
            type={'error'}
            theme='light'
            position={10}
            isGlobal
            onClose={() => setErrorMsg(undefined)}
          />
        )}

        <div className='mb-17'>
          <Datepicker
            defaultValue={formData.complete_date}
            label={translate(lang, 'Harvested date')}
            onChange={e =>
              e &&
              !disabled &&
              setFormData({ ...formData, complete_date: e.toDate().getTime() })
            }
            disabled={disabled}
          />
        </div>

        <div className={`mb-17 ${errors?.amount ? 'invalid-form' : ''}`}>
          <Input
            label={translate(lang, 'Harvested amount')}
            placeholder={translate(lang, 'Enter amount')}
            type='number'
            onChange={e => updateForm('amount', e.target.value)}
            value={numToStr(formData.amount)}
            disabled={disabled}
            unit={lineData.crop_type === 'OYSTER' ? 'pieces' : 'kg'}
            isErrorRes={!!errors?.amount}
          />
          {errors?.amount && (
            <div className='invalid-feedback'>{errors.amount}</div>
          )}
        </div>

        <div
          className={`mb-17 ${errors?.returned_client ? 'invalid-form' : ''}`}
        >
          {customers.length > 0 ? (
            <Dropdown
              label={translate(lang, 'Client Name')}
              options={customers.map(c => ({ id: c, label: c, value: c }))}
              value={formData.returned_client ?? undefined}
              onChange={v => !disabled && updateForm('returned_client', v)}
              disabled={disabled}
            />
          ) : (
            <Input
              label={translate(lang, 'Client Name')}
              placeholder={translate(lang, 'Enter client name return to')}
              type='text'
              onChange={e => updateForm('returned_client', e.target.value)}
              value={formData.returned_client ?? ''}
              disabled={disabled}
            />
          )}
          {errors?.returned_client && (
            <div className='invalid-feedback'>{errors.returned_client}</div>
          )}
        </div>

        <div className='mb-17'>
          <Dropdown
            label={translate(lang, 'Shell Condition')}
            options={conditionOptions.map(c => ({ id: c, label: c, value: c }))}
            value={formData.shell_condition ?? undefined}
            onChange={v => !disabled && updateForm('shell_condition', v)}
            disabled={disabled}
          />
        </div>

        <div
          className={`mb-17 d-flex ${
            errors?.shell_length ? 'invalid-form' : ''
          }`}
        >
          <div className='w-100 mr-7'>
            <Input
              label={translate(lang, 'Shell Length Min')}
              type='number'
              onChange={e => updateForm('shell_length', e.target.value)}
              value={numToStr(formData.shell_length)}
              disabled={disabled}
              unit='mm'
            />
            {errors?.shell_length && (
              <div className='invalid-feedback'>{errors.shell_length}</div>
            )}
          </div>
          <div className='w-100 ml-7'>
            <Input
              label={translate(lang, 'Shell Length Max')}
              type='number'
              onChange={e => updateForm('shell_length_max', e.target.value)}
              value={numToStr(formData.shell_length_max)}
              disabled={disabled}
              unit='mm'
            />
          </div>
        </div>

        <div className='mb-17'>
          <Input
            label={translate(lang, 'Comment')}
            placeholder={translate(lang, 'Enter any additional notes')}
            type='textarea'
            onChange={e => updateForm('comment', e.target.value)}
            value={formData.comment ?? ''}
            disabled={disabled}
          />
        </div>

        <div className='modal-button d-flex justify-end align-center mt-32'>
          <Button
            width={'small'}
            size={2}
            type='fill'
            color='green'
            onClick={confirmClick}
            disabled={disabled}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReturnHarvestModal;
